import { memo, useCallback, useMemo } from "react";
import { Card, Table, Button } from "react-bootstrap";
import { FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../Context/AppContext";
import { useGetPitchesByUser } from "../../api/queries/Pitch/useGetPitchesByUser";
import { useTranslation } from "react-i18next";

const ACTIVE_PITCH_STATUSES = ["Submitted", "Accepted", "Rejected"] as const;

function MyPitch() {
  const { t } = useTranslation();
  const { objectId } = useAppContext();
  const navigate = useNavigate();

  const { data: pitchesData, isPending: pitchesLoading } = useGetPitchesByUser({
    id: objectId || "",
    enabled: !!objectId,
  });

  const visiblePitches = useMemo(
    () =>
      pitchesData?.pitches?.filter((p) =>
        ACTIVE_PITCH_STATUSES.includes(p.pitch.status as (typeof ACTIVE_PITCH_STATUSES)[number])
      ) ?? [],
    [pitchesData]
  );

  const sortedPitches = useMemo(
    () => [...visiblePitches].sort((a, b) => a.pitch.id - b.pitch.id),
    [visiblePitches]
  );

  const onView = useCallback(
    (id: number) => {
      navigate(`/investing/pitch/${id}`);
    },
    [navigate]
  );

  return (
    <div className="container-fluid px-0 pb-4">
      <Card className="shadow-lg border-0 rounded-3 p-3 bg-white">
        <h5 className="fw-semibold mb-3">{t("pitch.myActivePitches")}</h5>

        {pitchesLoading ? (
          <div className="text-center py-5 text-muted">{t("dashboard.loading")}</div>
        ) : visiblePitches.length > 0 ? (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="fw-semibold">{t("admin.events.name")}</th>
                  <th className="fw-semibold">{t("pitch.statusLabel")}</th>
                  <th className="fw-semibold text-center">{t("admin.events.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedPitches.map((p) => (
                    <tr key={p.pitch.id}>
                      <td>{p.pitch.name || "—"}</td>
                      <td>
                        <span
                          className={`badge ${
                            p.pitch.status === "Submitted"
                              ? "bg-success"
                              : p.pitch.status === "Rejected"
                              ? "bg-danger"
                              : "bg-primary"
                          }`}
                        >
                          {p.pitch.status}
                        </span>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="btn-custom-small"
                          onClick={() => onView(p.pitch.id)}
                        >
                          <FaEye /> {t("pitch.view")}
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-5 text-muted">
            <i className="fa-regular fa-folder-open fs-3 mb-2"></i>
            <p className="mb-0">{t("pitch.noPitchesFound")}</p>
          </div>
        )}
      </Card>
    </div>
  );
}

const MyPitchMemo = memo(MyPitch);
MyPitchMemo.displayName = "MyPitch";
export default MyPitchMemo;
